/**
 * The file behind a WhatsApp media header — slice 9's half of the composer.
 *
 * **What this replaced, and why the replacement is not cosmetic.** Slice 7's
 * composer asked for a *media handle* in a text box, because nothing in Wend
 * could produce one: `waMediaHeaderSchema` required a handle, the submission
 * payload nested it under `example.header_handle`, and there was no upload path
 * anywhere. That arrangement produced a template carrying a pointer at bytes
 * Wend never held. A Meta review handle cannot be re-minted from itself, so
 * such a template can never be resubmitted after a rejection — the person has
 * to find the original PDF again, and the one thing certain about a marketing
 * team's original PDF is that nobody can find it. The picker exists so the
 * bytes are ours; the handle is derived from them, never the other way round.
 *
 * **The limits are stated before the picker, per format.** Image, document and
 * video have three different ceilings and three different accepted sets, none
 * of them slice 4's email attachment limits, so a single sentence about "media"
 * would be wrong for at least two of the three. `waMediaLimitLine` reads every
 * figure out of `WA_MEDIA_LIMITS`, and the line shown is the one for the format
 * that is actually selected. A limit a person discovers by being refused is a
 * limit that was not shown.
 *
 * **One author writes every refusal, and it is not this file.**
 * `findWaHeaderMediaRejection` decides and `describeWaMediaRejection` words it,
 * both from `@wend/shared` — the same pair the API refuses an upload with. The
 * rejected alternative was `Dropzone`'s built-in `accept`/`maxSizeBytes`
 * checking, which is free and reads well and writes its **own** sentences: it
 * would tell somebody offering a JPEG for a document header to "choose a PDF
 * file", where the contract tells them the file is fine and the *header format*
 * is what to change. Two authors of the same refusal drift, and they drift in
 * the direction of the less specific one.
 *
 * **What the browser may be trusted about, and what it may not.** `file.size`
 * is exact, so the size check runs here and saves somebody an 80 MB upload that
 * was always going to be refused. `file.type` is the browser's guess from the
 * *file extension* and is routinely empty; the server decides the type by
 * sniffing the bytes. So the type checks here run only when the browser named a
 * type, and this panel never claims to be the decision — a rename cannot get a
 * file past the server, and a missing `file.type` must not get a good file
 * refused here.
 *
 * **The review handle and the send media are named as two things, on screen.**
 * They are the same bytes reached by two references with two lifetimes, and
 * conflating them is how a template gets approved and then sends nothing. The
 * two sentences printed are `WA_REVIEW_HANDLE_EXPLAINER` and
 * `WA_SEND_MEDIA_EXPLAINER` from the contract, verbatim, because a second
 * account of the distinction written here would eventually disagree with the
 * types that enforce it.
 *
 * **`reviewHandle: null` is not a failure and is never drawn as one.** It means
 * no handle has been minted from these bytes yet — the normal state of a file
 * waiting for a submission — and it is a different statement from "the upload
 * failed" and a different statement again from "the file is missing". Only the
 * last of those makes a template unfixable, and only the last is drawn in `bad`.
 *
 * **Two holes it leaves deliberately.** It does not print the handle string
 * itself: it is opaque, it is long, and a person can do nothing with it. And it
 * does not predict whether a submission will re-mint — `shouldRemintReviewHandle`
 * exists and the API owns that call, so a screen that guessed it would sooner or
 * later contradict the request it describes. What is stated instead is the
 * invariant that holds either way: the file is here, so the handle can be made
 * again.
 *
 * **It imports no API client, and that is what makes it testable.** The upload
 * and the removal arrive as props from `WaComposer`, which owns the document
 * they change. The alternative — calling `uploadWaHeaderMedia` from here — was
 * written first and thrown away, because `lib/api/client.ts` reads
 * `import.meta.env` and therefore only evaluates inside Vite: a component that
 * touches it cannot be rendered by a verify script, and the sentences on this
 * panel *are* the deliverable. `verify-wa-header-media.tsx` renders it and reads
 * the words back, which is the same argument `verify-whatsapp-screens.tsx` makes
 * about `CampaignResultsPanel`, acted on rather than noted.
 *
 * ## Slice 17 — the two things that were not allowed to move
 *
 * **The per-format limits stay above the picker**, in both forms: the ceiling as
 * a mono figure in the header row, and `waMediaLimitLine(format)` — the accepted
 * types and the ceiling in words, for the format actually selected — as a full
 * sentence under it. Image, document and video have three different ceilings, so
 * one sentence about "media" would be wrong for two of them, and a limit a
 * person discovers by being refused is a limit that was not shown.
 *
 * **The review-handle / send-media distinction stays open.** Its title now
 * carries the whole claim — *this file is the sample Meta reviews, not what a
 * campaign sends* — so a skimmer who reads nothing else has it, and both
 * contract explainers stay underneath rather than folding. Conflating the two
 * produces a template that is approved and then sends nothing, and that failure
 * is invisible until a campaign runs. Only the introductory sentence went, and
 * only because the new title says what it said.
 *
 * What did fold is the archaeology: how a template comes to hold a handle with
 * no file behind it is slice 9's history, and it sits behind `MoreDetail` under
 * the warning that says what to do about it. The `StoredFile` reassurances kept
 * their load-bearing clause — *this is not a failed upload* — and lost the rest.
 *
 * **Remove now says what it destroys.** It deletes the stored file *and* clears
 * the header's handle, and a handle can never be minted again from bytes that
 * are gone. That was implied by a sentence about orphan sweeps; it is stated.
 */
import {
  WA_MEDIA_LIMITS,
  WA_REVIEW_HANDLE_EXPLAINER,
  WA_SEND_MEDIA_EXPLAINER,
  describeWaMediaRejection,
  findWaHeaderMediaRejection,
  formatWaMediaBytes,
  waHeaderMediaAccept,
  type WaHeaderMediaAsset,
  type WaMediaFormat,
  type WaMediaRejection,
} from '@wend/shared';
import { Badge, Button, Callout, formatFileSize } from '@wend/ui';
import { FileText, Image as ImageIcon, Trash2, Upload, Video } from 'lucide-react';
import { useRef, useState, type ComponentType } from 'react';

import { ErrorState } from '../../../components/ErrorState';
import { errorMessage } from '../../../lib/api/errors';
import { formatDateTime } from '../../../lib/format';
import { WA_HEADER_FORMAT_LABEL, waMediaLimitLine } from '../../../lib/whatsapp-display';
import { MoreDetail } from '../MoreDetail';

const FORMAT_ICON: Readonly<Record<WaMediaFormat, ComponentType<{ className?: string }>>> = {
  image: ImageIcon,
  document: FileText,
  video: Video,
};

/** `an image`, `a document`. Derived, so a fourth format needs no edit here. */
function anArticle(word: string): string {
  return /^[aeiou]/i.test(word) ? 'an' : 'a';
}

/**
 * What this panel can decide without the server, and what it must not.
 *
 * When the browser named a type, the contract's own gate answers the whole
 * question — empty, unsupported, wrong format for the slot, too large — in the
 * order a person can act on. When it named none (a `.mp4` the OS does not know,
 * a file dragged from an archive), the only thing answerable here is the size,
 * and answering the type question anyway would mean inventing a MIME type to
 * feed the checker. So the size arm builds its rejection from the same table the
 * checker reads, and the type question waits for the bytes to reach the server.
 *
 * **Exported for `verify-wa-header-media.tsx` and for nothing else.** It is the
 * gate that keeps a file Meta would refuse from ever reaching an upload session,
 * and `renderToStaticMarkup` cannot click a file input — so the only way to
 * assert it is to call it. A gate whose only coverage is "the component compiles"
 * is a gate nobody has checked.
 */
export function browserSideRejection(file: File, format: WaMediaFormat): WaMediaRejection | null {
  const declared = file.type.trim();
  if (declared !== '') {
    return findWaHeaderMediaRejection({ format, mimeType: declared, byteSize: file.size });
  }

  if (file.size <= 0) return { reason: 'empty_file', format };

  const limits = WA_MEDIA_LIMITS[format];
  if (file.size > limits.maxBytes) {
    return {
      reason: 'too_large',
      format,
      byteSize: file.size,
      maxBytes: limits.maxBytes,
      metaMaxBytes: limits.metaMaxBytes,
      limitOwner: limits.limitOwner,
    };
  }

  return null;
}

export interface WaHeaderMediaPanelProps {
  /** The header slot being filled. Chooses the ceiling, the types and the words. */
  format: WaMediaFormat;
  /** The stored file, or `null` when this template has none. */
  asset: WaHeaderMediaAsset | null;
  /** True only for the first read, when there is nothing to show yet. */
  loading: boolean;
  /** Non-null when the read itself failed — which is not "there is no file". */
  loadError: unknown;
  onRetry: () => void;
  /** Meta owns a submitted template: the file is read, and changed nowhere. */
  readOnly: boolean;
  /** The handle the document currently carries. `''` when it has none. */
  documentHandle: string;
  /**
   * Stores the file and rewires the document. Rejects on failure, and the
   * rejection is shown here — the format travels with the file so the ceiling
   * that was checked and the ceiling the server applies are the same one.
   */
  onUpload: (file: File, format: WaMediaFormat) => Promise<void>;
  /** Removes the stored file and clears the header's handle. Rejects on failure. */
  onRemove: (assetId: string) => Promise<void>;
}

export function WaHeaderMediaPanel({
  format,
  asset,
  loading,
  loadError,
  onRetry,
  readOnly,
  documentHandle,
  onUpload,
  onRemove,
}: WaHeaderMediaPanelProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  /**
   * The failure, with the heading that belongs to the action that failed.
   *
   * Two fields rather than one string, because "That file was not used" over a
   * failed *removal* is a sentence about the wrong event — and a person reading
   * it looks for a file problem that is not there.
   */
  const [failure, setFailure] = useState<{ title: string; message: string } | null>(null);

  const limits = WA_MEDIA_LIMITS[format];
  const Icon = FORMAT_ICON[format];
  const formatLabel = WA_HEADER_FORMAT_LABEL[format];

  // A file stored for one slot is not a file for another: the handle Meta gets
  // is minted for these bytes, so an image sitting under a document header would
  // have Meta review a picture and approve a PDF header.
  const matching = asset !== null && asset.format === format ? asset : null;
  const mismatched = asset !== null && asset.format !== format ? asset : null;

  async function upload(file: File): Promise<void> {
    const rejection = browserSideRejection(file, format);
    if (rejection !== null) {
      setFailure({ title: 'That file was not used', message: describeWaMediaRejection(rejection) });
      // Cleared so the same path can be offered again after a refusal —
      // without this, re-selecting an identical file fires no change event.
      if (inputRef.current !== null) inputRef.current.value = '';
      return;
    }

    setBusy(true);
    setFailure(null);
    try {
      await onUpload(file, format);
    } catch (cause) {
      setFailure({ title: 'That file was not used', message: errorMessage(cause) });
    } finally {
      setBusy(false);
      if (inputRef.current !== null) inputRef.current.value = '';
    }
  }

  async function remove(stored: WaHeaderMediaAsset): Promise<void> {
    setBusy(true);
    setFailure(null);
    try {
      await onRemove(stored.assetId);
    } catch (cause) {
      setFailure({ title: 'The file was not removed', message: errorMessage(cause) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-4 rounded-base border border-line bg-white/[0.02] p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h3 className="flex items-center gap-2 text-[0.95rem] text-text">
          <Icon aria-hidden="true" className="size-4 text-muted" />
          {formatLabel} header file
        </h3>
        {/* Stated here, before the picker is opened, and for this format only. */}
        <p className="font-mono text-[0.7rem] uppercase tracking-[0.1em] text-muted">
          {formatWaMediaBytes(limits.maxBytes)} max
        </p>
      </div>

      <p className="max-w-prose text-[0.88rem] leading-relaxed text-muted">
        {waMediaLimitLine(format)}
      </p>

      {/*
        Both halves are kind 2 and neither folds. The title carries the claim for
        a skimmer — this file is the sample Meta reviews, not the media a
        campaign sends — and the two explainers stay open underneath because
        conflating them is what produces a template that is approved and then
        sends nothing, which is a failure nobody sees until a campaign runs.
        Only the introductory sentence went; the title now says what it said.
      */}
      <Callout tone="info" title="This file is the sample Meta reviews, not what a campaign sends">
        <p>Reusing one where the other belongs is how a template is approved and then sends nothing.</p>
        <dl className="mt-2 flex flex-col gap-2">
          <div>
            <dt className="font-medium text-text">The handle Meta reviews</dt>
            <dd className="text-muted">{WA_REVIEW_HANDLE_EXPLAINER}</dd>
          </div>
          <div>
            <dt className="font-medium text-text">The media a message carries</dt>
            <dd className="text-muted">{WA_SEND_MEDIA_EXPLAINER}</dd>
          </div>
        </dl>
      </Callout>

      {loadError !== null && (
        <ErrorState
          error={loadError}
          title="Could not check for a stored file"
          onRetry={onRetry}
          className="flex flex-col items-start gap-3 rounded-base border border-bad/40 px-4 py-4"
        />
      )}

      {loading && loadError === null && (
        <p className="text-[0.85rem] text-muted">Checking for a stored file…</p>
      )}

      {!loading && loadError === null && (
        <>
          {matching !== null && <StoredFile asset={matching} />}

          {mismatched !== null && (
            <Callout tone="bad" title="The stored file is not this kind of header">
              This template holds {mismatched.filename} — {mismatched.mimeType}, which WhatsApp
              takes as {anArticle(WA_MEDIA_LIMITS[mismatched.format].noun)}{' '}
              {WA_MEDIA_LIMITS[mismatched.format].noun} header. This header is set to{' '}
              {formatLabel.toLowerCase()}, so Meta would review one thing and approve a header for
              another. Upload {anArticle(limits.noun)} {limits.noun}, or set the header format back
              to {WA_HEADER_FORMAT_LABEL[mismatched.format].toLowerCase()}.
            </Callout>
          )}

          {asset === null && documentHandle.trim() !== '' && (
            <Callout tone="warn" title="This header has a handle but no file behind it">
              Submitting is refused until a file is uploaded here, and a rejection cannot be
              answered without finding the original file. Wend does not have the bytes the stored
              handle was minted from, and a handle cannot be minted from itself.
              <MoreDetail summary="How a template ends up like this" className="mt-2">
                <p>
                  The handle was typed in before Wend could store the file — the arrangement slice
                  9 replaced. Upload the original and every handle from then on is minted from
                  Wend&rsquo;s own copy, which is what makes a rejection answerable.
                </p>
              </MoreDetail>
            </Callout>
          )}

          {asset === null && documentHandle.trim() === '' && (
            <p className="text-[0.88rem] text-muted">
              No file yet. A media header cannot be saved or submitted without one.
            </p>
          )}
        </>
      )}

      {failure !== null && (
        <Callout tone="bad" title={failure.title}>
          {failure.message}
        </Callout>
      )}

      {readOnly ? (
        <p className="text-[0.82rem] text-muted">
          Meta holds this template, so the header file cannot be changed. It is still here when a
          rejection reopens editing.
        </p>
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          <input
            ref={inputRef}
            type="file"
            className="sr-only"
            accept={waHeaderMediaAccept(format)}
            disabled={busy}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file !== undefined) void upload(file);
            }}
          />
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={busy}
            onClick={() => {
              inputRef.current?.click();
            }}
          >
            <Upload aria-hidden="true" className="size-4" />
            {busy ? 'Working…' : asset === null ? 'Choose a file' : 'Replace the file'}
          </Button>

          {asset !== null && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={busy}
              onClick={() => {
                void remove(asset);
              }}
            >
              <Trash2 aria-hidden="true" className="size-4" />
              {/* Named for a screen reader: "Remove" alone, beside a Replace, is
                  operable and says nothing about what it removes. */}
              <span className="sr-only">Remove the header file {asset.filename}</span>
              <span aria-hidden="true">Remove</span>
            </Button>
          )}
        </div>
      )}

      {/*
        What Remove destroys, beside the button. The handle-clearing half is the
        part a person cannot guess and cannot undo with a second click: the
        bytes are gone, so the handle minted from them can never be made again.
      */}
      {!readOnly && asset !== null && (
        <p className="text-[0.82rem] text-muted">
          Remove deletes the stored file and clears the header&rsquo;s handle. It cannot be undone
          from here — the file has to be uploaded again.
        </p>
      )}
    </div>
  );
}

/**
 * The stored file, and what has been made from it so far.
 *
 * The three states below are three different sentences on purpose. A file with
 * a handle is submittable; a file without one is stored and waiting, which is
 * normal and is drawn in the same neutral register; a file whose handle never
 * reached the document is the only one that blocks a save, and it says what to
 * do about it. Collapsing the last two into "no handle" would put a warning on
 * a template that has nothing wrong with it.
 */
function StoredFile({ asset }: { asset: WaHeaderMediaAsset }) {
  const Icon = FORMAT_ICON[asset.format];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-base border border-line px-3 py-2.5">
        <Icon aria-hidden="true" className="size-4 shrink-0 text-muted" />
        <span className="min-w-0 flex-1 truncate text-[0.88rem] text-text" title={asset.filename}>
          {asset.filename}
        </span>
        <span className="font-mono text-[0.7rem] tabular-nums text-muted">
          {formatFileSize(asset.byteSize)}
        </span>
        {asset.reviewHandle === null ? (
          <Badge tone="neutral">No sample handle yet</Badge>
        ) : (
          <Badge tone="good">Sample handle minted</Badge>
        )}
      </div>

      <p className="text-[0.82rem] leading-relaxed text-muted">
        {asset.mimeType} · uploaded {formatDateTime(asset.uploadedAt)}
        {asset.reviewHandle !== null &&
          ` · sample handle minted ${formatDateTime(asset.reviewHandle.mintedAt)}`}
      </p>

      {/*
        "Not a failed upload" is the load-bearing clause and it stays first. The
        bytes are here; what is missing is the handle, which is a normal state
        for a file waiting on a submission — a person who reads this as a lost
        file goes looking for the original, which is the one thing certain not to
        be findable.
      */}
      {asset.reviewHandle === null && (
        <Callout tone="warn" title="The file is stored, but no sample handle was minted">
          The bytes are safe here: this is not a failed upload, and the file does not need finding
          again. A media header cannot be saved until Meta has a handle minted from it — choosing
          the file again retries that.
        </Callout>
      )}

      <p className="text-[0.82rem] leading-relaxed text-muted">
        Wend keeps this file, not only the handle, so the handle is minted again from these bytes
        when it is resubmitted — nothing is uploaded from a laptop twice.
      </p>
    </div>
  );
}
